import { translate } from '../../../i18n';
import { UnchargeableCapacityPrice } from '../../court-management/hooks/useBatchUpsertAvailabilitiesCompact';

/**
 * The operator-facing sentence for prices a save accepted but no player can pay.
 *
 * The API returns these from the availability save: a price whose per-player
 * share (`price / capacity`, application fees included) lands inside Stripe's
 * 1–49 cent dead zone. Every booking at that capacity is refused with a typed
 * 400 (D25) — €9.00 split twenty ways is 45 cents a head — and until this
 * existed the only way the operator found out was a player failing to book.
 *
 * Returns `null` when there is nothing to say, so the caller keeps its plain
 * success toast: a warning that fires on an ordinary save is one the operator
 * learns to dismiss.
 */
export function buildUnchargeableCapacityWarning(
  rows: UnchargeableCapacityPrice[] | undefined,
): string | null {
  if (!rows || rows.length === 0) {
    return null;
  }

  const details = rows
    .map((row) =>
      // `fromWildcardRow` says the offending row is the all-capacities one
      // (`capacity = -1`), which prices every capacity the club offers. The
      // operator never typed this capacity and cannot find it by searching the
      // schedule, so naming the bare number sends them looking for a row that
      // does not exist. The server has always sent this flag and the sentence
      // ignored it. A string `replace` swaps only the FIRST occurrence, and the
      // wildcard variant names the capacity twice — hence the global regex.
      (row.fromWildcardRow
        ? translate('courts.unchargeable_capacity_price_row_wildcard')
        : translate('courts.unchargeable_capacity_price_row')
      )
        .replace('{{sport}}', String(row.sport))
        .replace(/\{\{capacity\}\}/g, String(row.capacity))
        .replace('{{price}}', String(row.price))
        .replace('{{cents}}', String(row.perPlayerCents)),
    )
    .join(' · ');

  return `${translate('courts.unchargeable_capacity_prices')} ${details}`;
}
